import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { Box, Avatar, Typography } from "@mui/material";
import axios from "utils/axios";
import { socketService } from "../services/socketService";
import { openSnackbar } from "api/snackbar";
import useAuth from "hooks/useAuth";
import { canAccessAdminPanel } from "utils/adminAccess";
import NotificationSoundManager from "utils/NotificationSoundManager";
import notificationSound from "assets/audio/notification.mp3";
import { getTypeAvatarProps } from "utils/notificationTypeIcon";
import { brand } from "brand/theme";

function getFullImageUrl(url) {
  if (!url) return undefined;
  if (url.startsWith("http")) return url;
  // VITE_APP_API_URL has no trailing slash in this project's .env
  // (e.g. "http://localhost") — strip one only if present, never
  // blindly chop the last character (that silently broke the hostname
  // itself, e.g. "http://localhost" -> "http://localhos").
  const baseUrl = import.meta.env.VITE_APP_API_URL.replace(/\/$/, "");
  return `${baseUrl}${url}`;
}

/** Avatar + name + text for admin toasts that have a known actor (SEC-019 WS follow-up). */
function ActorToastMessage({ name, avatarUrl, text }) {
  const displayName = name || "Utilisateur";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Avatar src={getFullImageUrl(avatarUrl)} sx={{ width: 28, height: 28, fontSize: 13 }}>
        {displayName.charAt(0).toUpperCase()}
      </Avatar>
      <Typography component="span" sx={{ fontSize: 13.5 }}>
        <Typography component="span" sx={{ fontWeight: 700, fontSize: "inherit" }}>
          {displayName}
        </Typography>{" "}
        {text}
      </Typography>
    </Box>
  );
}

/**
 * Icon + title for user-facing toasts of a type getTypeAvatarProps
 * recognizes (news/alert/avis) — no actor to show for these (it's
 * system-published content, not another user's action), so the type icon
 * stands in for one instead, matching the bell's own row styling (same
 * 3-line clamp, weight, line-height as Notification.jsx's title).
 */
function TypeToastMessage({ notification, text }) {
  const typeAvatar = getTypeAvatarProps(notification);
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      {typeAvatar && (
        <Box sx={{ position: "relative", width: 36, height: 36, flexShrink: 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${typeAvatar.color}1a`,
              color: typeAvatar.color,
            }}
          >
            {typeAvatar.iconSrc ? (
              <Box
                component="img"
                src={typeAvatar.iconSrc}
                alt=""
                sx={{ width: 17, height: 17, filter: brand.iconFilters.accent }}
              />
            ) : (
              <typeAvatar.Icon size={17} />
            )}
          </Box>
          {typeAvatar.statusColor && (
            <Box
              sx={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: 14,
                height: 14,
                borderRadius: "50%",
                bgcolor: typeAvatar.statusColor,
                border: "2px solid #fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {typeAvatar.StatusIcon && (
                <typeAvatar.StatusIcon size={8} color="#fff" strokeWidth={3} />
              )}
            </Box>
          )}
        </Box>
      )}
      <Typography
        component="span"
        sx={{
          fontSize: 13.5,
          fontWeight: 500,
          lineHeight: 1.37,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

// Create Notification Context
const NotificationContext = createContext();

// Local storage keys for notification persistence
const ADMIN_UNREAD_COUNT_KEY = "admin_unread_count";
const ADMIN_REPORT_COUNT_KEY = "admin_report_count";
const ADMIN_COMMENT_COUNT_KEY = "admin_comment_count";

// contributions/notifications.py's related_object.kind -> the plural route
// segment ContributionModerationHub expects (/admin/contributions/moderate/:type).
const CONTRIBUTION_KIND_TO_ROUTE = {
  formation: "formations",
  expert: "experts",
  institution: "institutions",
  event: "events",
  document: "documents",
  question: "expert-questions",
  medicament: "medicaments",
};

/**
 * Where the "Voir" button on a generic (new_notification) admin toast should
 * navigate. Mirrors notifications/serializers.py's NotificationSerializer —
 * `related_object` is the same shape sent there.
 */
function getAdminNotificationUrl(notification) {
  const related = notification?.related_object;
  switch (notification?.notification_type) {
    case "CONTRIBUTION_SUBMITTED": {
      const routeType = CONTRIBUTION_KIND_TO_ROUTE[related?.kind];
      if (routeType && related?.id) {
        return `/admin/contributions/moderate/${routeType}/${related.id}`;
      }
      return routeType ? `/admin/contributions/moderate/${routeType}` : "/admin/contributions";
    }
    case "INP_VERIFICATION":
      return "/admin/inp/verifications";
    default:
      if (related?.type === "avis") {
        return `/avis/${related.id}?admin_review=true`;
      }
      if (related?.type === "veille") {
        return `/veille/${related.id}`;
      }
      return "/admin/notifications";
  }
}

export const NotificationProvider = ({ children }) => {
  // State
  const [notifications, setNotifications] = useState([]);
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [adminUnreadCount, setAdminUnreadCount] = useState(0);
  const [adminReportCount, setAdminReportCount] = useState(0);
  const [adminCommentCount, setAdminCommentCount] = useState(0);
  // adminUnreadCount is ALSO set ad-hoc in several places below (initial WS
  // counts push, new_report/new_comment/new_notification/picture_upload
  // handlers, mark-read) — those predate the persisted AdminNotification
  // table and drift from it (e.g. they never account for contributions,
  // and accumulate +1 per live event independent of what's actually
  // unread). This effect is the single source of truth: the bell's badge
  // is always the unread count within adminNotifications itself, so any
  // stale/ad-hoc value set elsewhere gets corrected right after.
  const [isConnected, setIsConnected] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tokenExpiryTime, setTokenExpiryTime] = useState(0);
  // State to track if we should play sounds (only for new notifications via WebSocket)
  const [shouldPlaySound, setShouldPlaySound] = useState(false);
  // Shared notification preferences (sound, per-type toggles). Single
  // source of truth across every consumer — previously each component
  // (the header bell, the settings page) called its own independent
  // useGetNotificationPreferences() hook instance with no shared state, so
  // saving a change on the settings page never reached the bell: it kept
  // playing sound from whatever it had fetched at its own original mount.
  const [notificationPreferences, setNotificationPreferences] = useState(null);
  const [preferencesLoading, setPreferencesLoading] = useState(true);

  const { user, isLoggedIn } = useAuth();

  const refreshNotificationPreferences = useCallback(async () => {
    try {
      setPreferencesLoading(true);
      const { data } = await axios.get("/api/users/notification-preferences/");
      setNotificationPreferences(data);
      return data;
    } catch (error) {
      // Non-fatal — callers that only read this treat null/stale as "allow"
      // the same way the preference-check logic already does elsewhere.
      return null;
    } finally {
      setPreferencesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      refreshNotificationPreferences();
    }
  }, [isLoggedIn, refreshNotificationPreferences]);

  // Setup socket handlers
  const setupSocketHandlers = useCallback(() => {
    socketService.setHandlers({
      onConnect: () => {
        setIsConnected(true);
      },

      onDisconnect: () => {
        setIsConnected(false);
      },

      onTokenExpiry: (expiresIn) => {
        setTokenExpiryTime(expiresIn);
      },

      onUnreadCount: (count) => {
        setUnreadCount(count);
      },

      onNotificationsList: (notificationsList) => {
        // This is initial data load or refresh - don't play sound
        setNotifications(notificationsList);
        setShouldPlaySound(false);
      },

      onNewNotification: (notification, count) => {
        if (notification) {
          setNotifications((prev) => [notification, ...prev]);
          // This is a genuinely new notification via WebSocket - allow sound
          setShouldPlaySound(true);

          // Show toast notification for new notifications (custom snackbar).
          // related_object (NotificationSerializer) tells us where "Voir"
          // should go — e.g. a report resolution/dismissal notification
          // carries the reviewed avis/veille item, not the report itself.
          const related = notification.related_object;
          const relatedUrl =
            related?.type === "avis"
              ? `/avis/${related.id}`
              : related?.type === "veille"
                ? `/veille/${related.id}`
                : notification.notification_type === "INP_VERIFICATION"
                  ? notification.title?.includes("approuvée") || notification.title?.includes("rejetée")
                    ? "/verification"
                    : "/admin/inp/verifications"
                  : null;
          const rawMessage = notification.message || "";
          const truncatedMessage =
            rawMessage.length > 70 ? `${rawMessage.slice(0, 70).trimEnd()}…` : rawMessage;
          const toastText = `${notification.title} — ${truncatedMessage}`;
          const typeAvatar = getTypeAvatarProps(notification);
          openSnackbar({
            open: true,
            message: typeAvatar ? (
              <TypeToastMessage notification={notification} text={toastText} />
            ) : (
              toastText
            ),
            variant: "alert",
            alert: { color: "info" },
            autoHideDuration: relatedUrl ? 6000 : 4000,
            ...(relatedUrl
              ? {
                  actionLabel: "Voir",
                  onAction: () => {
                    window.location.href = relatedUrl;
                  },
                }
              : {}),
          });
        }
        if (count !== undefined) {
          setUnreadCount(count);
        }
      },

      onReadStatusUpdate: (notificationId, count) => {
        // Update notification read status
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId
              ? { ...notification, is_read: true }
              : notification,
          ),
        );

        if (count !== undefined) {
          setUnreadCount(count);
        }
      },

      onAllReadStatusUpdate: (count) => {
        // Mark all notifications as read
        setNotifications((prev) =>
          prev.map((notification) => ({ ...notification, is_read: true })),
        );

        setUnreadCount(0);
      },

      onError: (message) => {
        console.error("Socket error:", message);
        // Handle error display to user
        openSnackbar({
          open: true,
          message: `Erreur de connexion: ${message}`,
          variant: "alert",
          alert: { color: "error" },
          autoHideDuration: 3000,
        });
      },
    });

    // Admin handlers
    socketService.setAdminHandlers({
      onConnect: () => {
        setIsAdmin(true);
      },

      onDisconnect: () => {
        setIsAdmin(false);
      },

      onMessage: (data) => {
        // Handle admin-specific notifications
        // Handle initial counts from server
        if (data.type === "initial_counts") {

          // Update report and comment counts with exact numbers from server
          if (data.report_count !== undefined) {
            setAdminReportCount(data.report_count);
            localStorage.setItem(ADMIN_REPORT_COUNT_KEY, data.report_count);
          }

          if (data.comment_count !== undefined) {
            setAdminCommentCount(data.comment_count);
            localStorage.setItem(ADMIN_COMMENT_COUNT_KEY, data.comment_count);
          }

          // Set combined unread count for badge using exact values
          if (
            data.report_count !== undefined &&
            data.comment_count !== undefined
          ) {
            const totalUnread = data.report_count + data.comment_count;
            setAdminUnreadCount(totalUnread);
            localStorage.setItem(ADMIN_UNREAD_COUNT_KEY, totalUnread);
          }

          return;
        }

        // Handle full notifications list when requested (panel open)
        if (data.type === "notifications_list") {
          const list = Array.isArray(data.notifications)
            ? data.notifications
            : [];
          setAdminNotifications(list);
          // Optionally update unread badge to number of items
          setAdminUnreadCount((prev) =>
            typeof data.unread_count === "number"
              ? data.unread_count
              : list.length,
          );
          return;
        }

        // Handle counts update acknowledgements
        if (data.type === "counts_update") {
          if (typeof data.report_count === "number") {
            setAdminReportCount(data.report_count);
            localStorage.setItem(ADMIN_REPORT_COUNT_KEY, data.report_count);
          }
          if (typeof data.comment_count === "number") {
            setAdminCommentCount(data.comment_count);
            localStorage.setItem(ADMIN_COMMENT_COUNT_KEY, data.comment_count);
          }
          if (
            typeof data.report_count === "number" &&
            typeof data.comment_count === "number"
          ) {
            const totalUnread = data.report_count + data.comment_count;
            setAdminUnreadCount(totalUnread);
            localStorage.setItem(ADMIN_UNREAD_COUNT_KEY, totalUnread);
          }
          return;
        }

        if (data.type === "new_notification") {
          // Add the new admin notification
          if (data.notification) {
            setAdminNotifications((prev) => [data.notification, ...prev]);
            // This is a genuinely new admin notification via WebSocket - allow sound
            setShouldPlaySound(true);

            // Single toast for every admin event (SEC-019 WS follow-up): this
            // used to also pop a separate NotificationToast card, which
            // duplicated this same event and fell back to "Utilisateur"
            // because it read the name from notification.data.user instead
            // of the pre-formatted title/message strings the backend already
            // sends — those always have the real name baked in.
            openSnackbar({
              open: true,
              message: `${data.notification.title} — ${(
                data.notification.message || ""
              ).substring(0, 60)}`,
              variant: "alert",
              alert: { color: "info" },
              autoHideDuration: 6000,
              actionLabel: "Voir",
              onAction: () => {
                window.location.href = getAdminNotificationUrl(
                  data.notification,
                );
              },
            });

            // Update admin unread count
            if (data.unread_count !== undefined) {
              setAdminUnreadCount(data.unread_count);
            } else {
              setAdminUnreadCount((prev) => prev + 1);
            }
          }
        } else if (data.type === "new_report") {
          // Process report notifications
          if (data.report) {
            // Create a notification object from the report data
            const reportNotification = {
              id: data.report.id,
              title: `${data.report.report_type}`,
              message: `Un contenu a été signalé: "${data.report.content_object?.title || "Sans titre"}"`,
              notification_type: "REPORT",
              is_read: false,
              created_at: data.report.created_at,
              data: {
                report_id: data.report.id,
                report_type: data.report.report_type,
                content_type: data.report.content_type,
                content_id: data.report.content_object?.id,
                content_object: data.report.content_object,
                reporter: data.report.reporter,
                description: data.report.description,
              },
            };

            // Add to admin notifications
            setAdminNotifications((prev) => [reportNotification, ...prev]);
            // This is a genuinely new report notification via WebSocket - allow sound
            setShouldPlaySound(true);

            // Show toast notification for the admin
            openSnackbar({
              open: true,
              message: (
                <ActorToastMessage
                  name={data.report.reporter?.username}
                  avatarUrl={data.report.reporter?.profile_image}
                  text={`a signalé un contenu (${reportNotification.title})`}
                />
              ),
              variant: "alert",
              alert: { color: "warning" },
              autoHideDuration: 6000,
              actionLabel: "Voir",
              onAction: () => {
                window.location.href = `/admin/reports/${data.report.id}`;
              },
            });

            // Update admin report count
            if (data.report_count !== undefined) {
              setAdminReportCount(data.report_count);
              localStorage.setItem(ADMIN_REPORT_COUNT_KEY, data.report_count);
            } else {
              const newCount = adminReportCount + 1;
              setAdminReportCount(newCount);
              localStorage.setItem(ADMIN_REPORT_COUNT_KEY, newCount);
            }
          }
        } else if (data.type === "new_comment") {
          // Process comment notifications
          if (data.comment) {
            // Create a notification object from the comment data
            const commentNotification = {
              id: data.comment.id,
              title: `${data.comment.content?.substring(0, 30)}...`,
              message: `Une nouvelle contribution requiert votre modération`,
              notification_type: "COMMENT",
              is_read: false,
              created_at: data.comment.created_at || new Date().toISOString(),
              data: {
                comment_id: data.comment.id,
                content_type: "comment",
                content: data.comment.content,
                content_object: data.comment.content_object,
                user: data.comment.user,
                author_id: data.comment.user?.id,
                author_name: data.comment.user?.full_name,
              },
            };

            // Add to admin notifications
            setAdminNotifications((prev) => [commentNotification, ...prev]);
            // Show toast notification for the admin
            openSnackbar({
              open: true,
              message: (
                <ActorToastMessage
                  name={data.comment.user?.full_name || data.comment.user?.username}
                  avatarUrl={data.comment.user?.profile_image_url}
                  text="a soumis une nouvelle contribution à modérer"
                />
              ),
              variant: "alert",
              alert: { color: "info" },
              autoHideDuration: 6000,
              actionLabel: "Voir",
              onAction: () => {
                window.location.href = `/admin/contributions/${data.comment.id}`;
              },
            });

            // This is a genuinely new comment notification via WebSocket - allow sound
            setShouldPlaySound(true);

            // Update admin comment count
            if (data.comment_count !== undefined) {
              setAdminCommentCount(data.comment_count);
              localStorage.setItem(ADMIN_COMMENT_COUNT_KEY, data.comment_count);
            } else {
              const newCount = adminCommentCount + 1;
              setAdminCommentCount(newCount);
              localStorage.setItem(ADMIN_COMMENT_COUNT_KEY, newCount);
            }

            // Update total unread count
            setAdminUnreadCount((prev) => prev + 1);
            localStorage.setItem(ADMIN_UNREAD_COUNT_KEY, adminUnreadCount + 1);
          }
        } else if (data.type === "picture_upload") {
          // Profile picture pending validation. This used to be handled
          // ad-hoc inside the admin notifications page itself (only fired
          // while that page happened to be open) — centralizing it here so
          // it reaches the bell and the toast from anywhere in the admin.
          const pictureNotification = {
            id: `picture-${data.user_id}-${Date.now()}`,
            title: data.user_name || "Nouvelle photo de profil",
            message: data.message || "a téléchargé une nouvelle photo de profil",
            notification_type: "PICTURE",
            is_read: false,
            created_at: new Date().toISOString(),
            data: {
              type: "picture",
              user_id: data.user_id,
              user_email: data.user_email,
              user_name: data.user_name,
              user_title: data.user_title,
              // The user's existing (already-seen) avatar — shown as the
              // actor avatar, same as everywhere else. The newly-submitted
              // image is the thing awaiting review, kept separate.
              old_profile_image_url: data.old_profile_image_url,
              profile_image_url: data.profile_image_url,
            },
          };

          setAdminNotifications((prev) => [pictureNotification, ...prev]);
          setShouldPlaySound(true);
          setAdminUnreadCount((prev) => prev + 1);

          openSnackbar({
            open: true,
            message: (
              <ActorToastMessage
                name={data.user_name}
                avatarUrl={data.old_profile_image_url}
                text="a téléchargé une nouvelle photo de profil"
              />
            ),
            variant: "alert",
            alert: { color: "info" },
            autoHideDuration: 6000,
            actionLabel: "Voir",
            onAction: () => {
              window.location.href = "/admin/notifications?tab=3";
            },
          });
        } else if (data.type === "profession_change_request") {
          // New profession change request pending review — same shape as
          // picture_upload above, just for the "Professions" tab (index 6).
          const professionChangeNotification = {
            id: `profession-change-${data.request_id}-${Date.now()}`,
            title: data.user_name || "Changement de profession",
            message: data.message || "a demandé un changement de profession",
            notification_type: "PROFESSION_CHANGE",
            is_read: false,
            created_at: new Date().toISOString(),
            data: {
              type: "profession_change",
              request_id: data.request_id,
              user_id: data.user_id,
              user_email: data.user_email,
              user_name: data.user_name,
              user_title: data.user_title,
              user_avatar_url: data.user_avatar_url,
              old_profession: data.old_profession,
              new_profession: data.new_profession,
            },
          };

          setAdminNotifications((prev) => [professionChangeNotification, ...prev]);
          setShouldPlaySound(true);
          setAdminUnreadCount((prev) => prev + 1);

          openSnackbar({
            open: true,
            message: (
              <ActorToastMessage
                name={data.user_name}
                avatarUrl={data.user_avatar_url}
                text={`souhaite changer sa profession vers ${data.new_profession}`}
              />
            ),
            variant: "alert",
            alert: { color: "info" },
            autoHideDuration: 6000,
            actionLabel: "Voir",
            onAction: () => {
              window.location.href = `/admin/requests/profession-change/${data.request_id}`;
            },
          });
        } else if (data.type === "new_inp_verification") {
          // New INP verification request pending review — forwarded by
          // AdminNotificationsConsumer.inp_verification_request (fired from
          // submit_inp_verification, only when status === 'pending').
          const inpNotification = {
            id: `inp-verification-${data.verification_id}-${Date.now()}`,
            title: data.user_name || "Vérification INPE",
            message: data.message || "a soumis une demande de vérification INPE",
            notification_type: "INP_VERIFICATION",
            is_read: false,
            created_at: new Date().toISOString(),
            data: {
              type: "inp_verification",
              request_id: data.verification_id,
              user_id: data.user_id,
              user_email: data.user_email,
              user_name: data.user_name,
              user_avatar_url: data.user_avatar_url,
              submission_type: data.submission_type,
            },
          };

          setAdminNotifications((prev) => [inpNotification, ...prev]);
          setShouldPlaySound(true);
          setAdminUnreadCount((prev) => prev + 1);

          openSnackbar({
            open: true,
            message: (
              <ActorToastMessage
                name={data.user_name}
                avatarUrl={data.user_avatar_url}
                text="a soumis une demande de vérification INPE"
              />
            ),
            variant: "alert",
            alert: { color: "info" },
            autoHideDuration: 6000,
            actionLabel: "Voir",
            onAction: () => {
              window.location.href = `/admin/notifications?tab=7&id=${data.verification_id}`;
            },
          });
        }
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove dependencies to prevent infinite loop

  // Initialize WebSocket connection
  useEffect(() => {
    const userIsAdmin = user ? canAccessAdminPanel(user) : false;
    if (user) {
      setIsAdmin(userIsAdmin);
    }
    if (isLoggedIn) {
      // Setup socket handlers
      setupSocketHandlers();

      // Connect to WebSocket — auth now rides the httpOnly access_token
      // cookie automatically (SEC-019). Only attempt the admin socket for
      // users who plausibly are one, so ordinary users don't trigger a
      // doomed connection + unsuppressable browser console error.
      socketService.connect(userIsAdmin);

      // Cleanup on unmount
      return () => {
        socketService.disconnect();
      };
    }
  }, [isLoggedIn, user, setupSocketHandlers]);

  // Function to mark notification as read
  const markAsRead = useCallback((notificationId) => {
    socketService.markAsRead(notificationId);

    // Optimistic update
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId
          ? { ...notification, is_read: true }
          : notification,
      ),
    );
  }, []);

  // Function to mark admin notification as read
  const markAdminNotificationAsRead = useCallback((notificationId) => {
    // Optimistic update
    setAdminNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId
          ? { ...notification, is_read: true }
          : notification,
      ),
    );

    // Decrement unread count
    setAdminUnreadCount((prev) => Math.max(0, prev - 1));

    // Persist for real — AdminNotification.is_read in the DB, so it
    // survives a reload/reopen instead of reverting the moment the list
    // is refetched (the WS mark_as_read command below never persisted
    // anything; it only recomputes report/comment counts for the badge).
    axios
      .post(`/api/admin/notifications/${notificationId}/mark-read/`)
      .catch(() => {
        // Non-fatal — local state already reflects "read"; worst case a
        // later refetch shows it unread again.
      });

    if (socketService.isUserAdmin()) {
      socketService.sendAdminCommand({
        command: "mark_as_read",
        id: notificationId,
      });
    }
  }, []);

  // Function to mark all notifications as read
  const markAllAsRead = useCallback(() => {
    socketService.markAllAsRead();

    // Optimistic update
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, is_read: true })),
    );
    setUnreadCount(0);
  }, []);

  // Function to mark all admin notifications as read
  const markAllAdminNotificationsAsRead = useCallback(() => {
    if (socketService.isUserAdmin()) {
      socketService.sendAdminCommand({
        command: "mark_all_read",
      });

      // Optimistic update
      setAdminNotifications((prev) =>
        prev.map((notification) => ({ ...notification, is_read: true })),
      );
      setAdminUnreadCount(0);
    }
  }, []);

  // Function to refresh notifications
  const refreshNotifications = useCallback(() => {
    // This is a manual refresh - don't play sound
    setShouldPlaySound(false);
    socketService.getNotifications();
  }, []);

  // Function to refresh admin notifications — fetches the persisted "last
  // 50" feed over REST (real is_read status from the DB) rather than the
  // WS get_notifications command, which only ever hardcoded is_read: false
  // and depended on the admin WS socket already being connected. Live
  // updates still arrive over the socket (new_report/new_comment handlers
  // above); this is just for the initial/historical load.
  useEffect(() => {
    setAdminUnreadCount(adminNotifications.filter((n) => !n.is_read).length);
  }, [adminNotifications]);

  // Sound playback lives here, not in the bell UI components, because
  // those only mount conditionally based on viewport width (TopHeader.jsx
  // renders AdminNotificationSection/Notification only when !downXS, and
  // the narrow-screen MobileHeaderMenu has no sound logic at all) — on a
  // narrow screen neither bell exists in the DOM, so a useEffect living
  // inside either one never runs. shouldPlaySound is still set correctly
  // by the WS handlers above regardless of viewport (that's why the toast,
  // which also fires from here, always worked) — only the sound itself was
  // silently dropped on narrow screens. Centralizing here, in the always-
  // mounted provider, makes sound viewport-independent too.
  useEffect(() => {
    NotificationSoundManager.initialize(notificationSound);
  }, []);

  useEffect(() => {
    if (shouldPlaySound) {
      const canPlaySound =
        notificationPreferences?.sound_notifications !== undefined
          ? notificationPreferences.sound_notifications
          : true;
      if (canPlaySound) {
        NotificationSoundManager.playSound();
      }
      setShouldPlaySound(false);
    }
  }, [shouldPlaySound, notificationPreferences]);

  const refreshAdminNotifications = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setShouldPlaySound(false);
      const { data } = await axios.get("/api/admin/notifications/recent/");
      setAdminNotifications(data);
    } catch (error) {
      // Non-fatal — the bell just stays on whatever it already had
      // (live WS pushes, or empty on first load).
    }
  }, [isAdmin]);

  // Populate the bell's badge/list as soon as we know the user is an admin,
  // not only once they open the bell — otherwise the badge sits on a stale
  // ad-hoc count (or 0) until the first manual open.
  useEffect(() => {
    if (isAdmin) refreshAdminNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Function to reset shouldPlaySound flag (called after sound is played)
  const resetShouldPlaySound = useCallback(() => {
    setShouldPlaySound(false);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        adminNotifications,
        unreadCount,
        adminUnreadCount,
        isConnected,
        isAdmin,
        tokenExpiryTime,
        markAsRead,
        markAdminNotificationAsRead,
        markAllAsRead,
        markAllAdminNotificationsAsRead,
        refreshNotifications,
        refreshAdminNotifications,
        shouldPlaySound,
        resetShouldPlaySound,
        notificationPreferences,
        preferencesLoading,
        refreshNotificationPreferences,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook for using the notification context
export const useNotifications = () => useContext(NotificationContext);

export default NotificationContext;
